import { Card, CardContent, Combobox } from '@ibsys/kit';
import type { ComboboxItem } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { STRATEGIST_CONCEPT_ROLE } from '@/entities/strategist';
import type { StrategistTargetAttribute } from '@/entities/strategist';
import { TargetMetricPicker } from '@/features/strategist-target';
import { ThresholdSlider } from '@/shared/ui';

/** Подписи известных категорий понятий; незнакомая категория показывается своим кодом. */
const CATEGORY_LABEL_KEYS = {
  [STRATEGIST_CONCEPT_ROLE.objection]: 'hierarchy.categories.возражение',
  [STRATEGIST_CONCEPT_ROLE.argument]: 'hierarchy.categories.аргумент',
  [STRATEGIST_CONCEPT_ROLE.question]: 'hierarchy.categories.вопрос',
} as const;

type Props = {
  /** Текущая целевая метрика; `undefined` — метрика не выбрана */
  target: StrategistTargetAttribute | undefined;
  /** Смена целевой метрики; `undefined` — метрика снята */
  onTargetChange: (next: StrategistTargetAttribute | undefined) => void;
  /** Доступные категории (роли концептов) из ответа */
  categories: string[];
  /** Текущая категория среза */
  category: string | undefined;
  onCategoryChange: (value: string) => void;
  /** Доступные пороги близости (шаги ползунка) */
  thresholds: number[];
  /** Текущий порог близости */
  threshold: number | undefined;
  onThresholdChange: (value: number) => void;
};

/**
 * Управление страницы «Структурные единицы»: целевая метрика, категория понятий и
 * порог близости.
 */
export const HierarchyControls: FC<Props> = ({
  target,
  onTargetChange,
  categories,
  category,
  onCategoryChange,
  thresholds,
  threshold,
  onThresholdChange,
}) => {
  const { t } = useTranslation('strategist');

  const categoryOptions = useMemo<ComboboxItem[]>(
    () =>
      categories.map((item) => {
        const key = CATEGORY_LABEL_KEYS[item as keyof typeof CATEGORY_LABEL_KEYS];
        return { value: item, label: key ? t(key) : item };
      }),
    [categories, t],
  );

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end lg:gap-8">
        <TargetMetricPicker isNoneAllowed value={target} onChange={onTargetChange} />

        <Combobox
          label={t('hierarchy.category.label')}
          options={categoryOptions}
          value={category}
          onChange={(next?: string | number) => {
            // Категорию нельзя снять: пустой выбор оставляет текущий срез.
            if (next !== undefined) onCategoryChange(String(next));
          }}
          isSearchable={false}
          className="lg:w-52"
        />

        <ThresholdSlider
          label={t('hierarchy.threshold.label')}
          thresholds={thresholds}
          value={threshold}
          onChange={onThresholdChange}
          coarserLabel={t('hierarchy.threshold.coarser')}
          finerLabel={t('hierarchy.threshold.finer')}
          className="flex flex-col gap-2 lg:w-80"
        />
      </CardContent>
    </Card>
  );
};
